import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Container,
  Typography,
  Paper,
  Select,
  FormControl,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  Chip,
  TableRow,
  Dialog, TableContainer,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  CircularProgress,
  Snackbar,
  Alert,
  Fade,
  Pagination,
  Box,
  TextField,
  InputLabel,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import InfoIcon from "@mui/icons-material/Info";
import base64 from "base64-js";

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: 12,
  boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
  background: "linear-gradient(145deg, #ffffff, #f9f9f9)",
}));

const StyledButton = styled(Button)(({ theme }) => ({
  borderRadius: 20,
  padding: "8px 24px",
  textTransform: "none",
  fontWeight: 600,
  transition: "all 0.3s ease",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
  },
}));

function RouteVehicleMapping() {
  const [showUserGuide, setShowUserGuide] = useState(false);
  const [vehicles, setVehicles] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [selectedVehicles, setSelectedVehicles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [routeAssignments, setRouteAssignments] = useState({});
  const [currentSelections, setCurrentSelections] = useState({});
  const [routeRemovals, setRouteRemovals] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize] = useState(5);
  const [totalRecords, setTotalRecords] = useState(0);
  const [existingMappings, setExistingMappings] = useState({});
  const [selectedVehicleForView, setSelectedVehicleForView] = useState("");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [authorization, setAuthorization] = useState(null);
  const [canEdit, setCanEdit] = useState(false);
  const [canSave, setCanSave] = useState(false);
  const { encodedFormId } = useParams();

  const decodeFormId = (encodedFormId) => {
    if (!encodedFormId || typeof encodedFormId !== "string") {
      console.error("encodedFormId is missing or invalid:", encodedFormId);
      return null;
    }
    try {
      const paddedEncodedFormId = encodedFormId.padEnd(Math.ceil(encodedFormId.length / 4) * 4, "=");
      const bytes = base64.toByteArray(paddedEncodedFormId);
      return new TextDecoder().decode(bytes);
    } catch (error) {
      console.error("Error decoding formId:", error);
      return null;
    }
  };

  const formId = decodeFormId(encodedFormId);

  const Authorizer = async () => {
    if (!formId) {
      console.error("formId is null, cannot call Authorizer");
      setAuthorization({ isPermission: 0 });
      return;
    }
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem("token");
      const response = await fetch(`${apiUrl}/CPanel/Module_Authorizer`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify({
          formId,
        }),
      });
      if (response.ok) {
        const responseData = await response.json();
        setAuthorization(responseData[0]);
        setCanEdit(responseData[0].uModify === 1);
        setCanSave(responseData[0].uWrite === 1);
      } else {
        console.error("Authorization failed:", response.status);
        setSnackbar({
          open: true,
          message: "Invalid authorization response.",
          severity: "error",
        });
      }
    } catch (error) {
      console.error("API request error:", error);
      setSnackbar({
        open: true,
        message: "An error occurred during authorization.",
        severity: "error",
      });
    }
  };

  const fetchVehicles = useCallback(async () => {
    try {
      setLoading(true);
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const response = await fetch(`${apiUrl}/Transportation/VehicleMaster/GetVehicleMaster`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: sessionStorage.getItem("token"),
        },
      });
      if (!response.ok) throw new Error(`Error: ${response.status}`);
      const data = await response.json();
      const vehicleList = Array.isArray(data) ? data : [];
      if (vehicleList.length === 0) {
        setVehicles([]);
        setTotalRecords(0);
        setSnackbar({
          open: true,
          message: "No vehicles found in the system.",
          severity: "info",
        });
      } else {
        const filteredVehicles = vehicleList.filter(vehicle =>
          !searchTerm ||
          vehicle.registrationNo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          vehicle.make?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          vehicle.model?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          vehicle.chassesNo?.toLowerCase().includes(searchTerm.toLowerCase())
        );
        setVehicles(filteredVehicles);
        setTotalRecords(filteredVehicles.length);
        if (filteredVehicles.length === 0) {
          setSnackbar({
            open: true,
            message: "No vehicles match your search.",
            severity: "info",
          });
        }
        await fetchMappingsForAllVehicles(filteredVehicles);
      }
    } catch (error) {
      console.error("Error fetching vehicles:", error);
      setVehicles([]);
      setTotalRecords(0);
      setSnackbar({
        open: true,
        message: "Failed to load vehicles. Please try again later.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  }, [searchTerm]);

  const handleTempRouteChange = (vehicleID, routeID) => {
    setCurrentSelections((prev) => ({
      ...prev,
      [vehicleID]: routeID
    }));
  };

  const handleAddRoute = (vehicleID) => {
    const selectedRouteID = currentSelections[vehicleID];
    if (!selectedRouteID) return;

    const existingRoutes = existingMappings[vehicleID]?.map(m => m.routeId) || [];
    const addedRoutes = routeAssignments[vehicleID] || [];

    if (existingRoutes.includes(parseInt(selectedRouteID)) || addedRoutes.includes(parseInt(selectedRouteID))) {
      setSnackbar({
        open: true,
        message: "This route is already assigned to the vehicle.",
        severity: "warning",
      });
      return;
    }

    setRouteAssignments((prev) => ({
      ...prev,
      [vehicleID]: [
        ...(prev[vehicleID] || []),
        parseInt(selectedRouteID)
      ]
    }));

    setCurrentSelections((prev) => ({
      ...prev,
      [vehicleID]: ""
    }));
  };

  const fetchRoutes = useCallback(async () => {
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const response = await fetch(`${apiUrl}/Transportation/Route/GetRoutes`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: sessionStorage.getItem("token"),
        },
        body: JSON.stringify({
          searchTerm: "",
          pageNumber: 1,
          pageSize: 100
        }),
      });
      if (!response.ok) throw new Error(`Error: ${response.status}`);
      const data = await response.json();
      if (data && data.routes && data.routes.length > 0) {
        setRoutes(data.routes);
      } else {
        setRoutes([]);
        setSnackbar({
          open: true,
          message: "No routes found",
          severity: "info",
        });
      }
    } catch (error) {
      console.error("Error fetching routes:", error);
      setSnackbar({
        open: true,
        message: "Failed to load routes",
        severity: "error",
      });
    }
  }, []);

  const fetchMappingsForAllVehicles = useCallback(async (vehicleList) => {
    try {
      const mappings = {};
      const promises = vehicleList.map(async (vehicle) => {
        if (vehicle.vechileID && vehicle.vechileID !== 0) {
          try {
            const apiUrl = process.env.REACT_APP_BASE_URL;
            const token = sessionStorage.getItem("token");
            const response = await fetch(`${apiUrl}/Transportation/RouteMappingVehicle/routevehicles`, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: token,
              },
              body: JSON.stringify({
                vehicleId: parseInt(vehicle.vechileID),
                searchTerm: "",
              }),
            });
            if (response.ok) {
              const data = await response.json();
              if (data.mappings && data.mappings.length > 0) {
                const activeMappings = data.mappings.filter(mapping =>
                  mapping.assignedStatus === true &&
                  (mapping.vechileID === parseInt(vehicle.vechileID) ||
                    (mapping.vechileID === 0 && mapping.registrationNo === vehicle.registrationNo))
                );
                if (activeMappings.length > 0) {
                  mappings[vehicle.vechileID] = activeMappings.map(mapping => ({
                    routeId: mapping.routeId,
                    routeName: mapping.routeName,
                    routeVehicleID: mapping.routeVehicleID,
                    routeCharge: mapping.routeCharge,
                    distance: mapping.distance,
                    description: mapping.description
                  }));
                }
              }
            }
          } catch (error) {
            console.error(`Error fetching mappings for vehicle ${vehicle.vechileID}:`, error);
          }
        }
      });
      await Promise.all(promises);
      setExistingMappings(mappings);
    } catch (error) {
      console.error("Error fetching mappings for all vehicles:", error);
    }
  }, []);

  const fetchRouteVehicleMappings = useCallback(async (vehicleId) => {
    if (!vehicleId || vehicleId === 0) {
      console.log("Invalid vehicle ID:", vehicleId);
      return;
    }
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem("token");
      const response = await fetch(`${apiUrl}/Transportation/RouteMappingVehicle/routevehicles`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify({
          vehicleId: parseInt(vehicleId),
          searchTerm: "",
        }),
      });
      if (!response.ok) throw new Error(`Error: ${response.status}`);
      const data = await response.json();
      if (data.mappings && data.mappings.length > 0) {
        const activeMappings = data.mappings.filter(mapping =>
          mapping.assignedStatus === true &&
          (mapping.vechileID === parseInt(vehicleId) ||
            (mapping.vechileID === 0 && mapping.registrationNo))
        );
        if (activeMappings.length > 0) {
          const mappingData = activeMappings.map(mapping => ({
            routeId: mapping.routeId,
            routeName: mapping.routeName,
            routeVehicleID: mapping.routeVehicleID,
            routeCharge: mapping.routeCharge,
            distance: mapping.distance,
            description: mapping.description
          }));
          setExistingMappings(prev => ({
            ...prev,
            [parseInt(vehicleId)]: mappingData
          }));
        } else {
          setExistingMappings(prev => ({
            ...prev,
            [parseInt(vehicleId)]: []
          }));
        }
      } else {
        setExistingMappings(prev => ({
          ...prev,
          [parseInt(vehicleId)]: []
        }));
      }
    } catch (error) {
      console.error("Error fetching route-vehicle mappings for vehicle", vehicleId, ":", error);
    }
  }, []);

  const handleRouteAssignment = useCallback((vehicleId, routeId) => {
    setRouteAssignments(prev => ({
      ...prev,
      [vehicleId]: routeId ? routes.find(r => r.routeID === parseInt(routeId)) : null
    }));
  }, [routes]);

  const handleVehicleSelection = useCallback((vehicleId, checked) => {
    setSelectedVehicles(prev => {
      if (checked) {
        return [...prev, vehicleId];
      } else {
        return prev.filter(id => id !== vehicleId);
      }
    });
  }, []);

  const handleSelectAll = useCallback((checked) => {
    if (checked) {
      const currentPageVehicles = paginatedVehicles.map(v => v.vechileID);
      setSelectedVehicles(prev => [...new Set([...prev, ...currentPageVehicles])]);
    } else {
      const currentPageVehicles = paginatedVehicles.map(v => v.vechileID);
      setSelectedVehicles(prev => prev.filter(id => !currentPageVehicles.includes(id)));
    }
  }, []);

  const handleSave = useCallback(async () => {
    if (!canSave) {
      setSnackbar({
        open: true,
        message: "You don't have permission to save assignments.",
        severity: "error",
      });
      return;
    }

    if (selectedVehicles.length === 0) {
      setSnackbar({
        open: true,
        message: "Please select vehicles",
        severity: "warning",
      });
      return;
    }

    try {
      setLoading(true);
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem("token");
      const payload = [];

      selectedVehicles.forEach(vehicleId => {
        let currentRoutes = [...(existingMappings[vehicleId] || []).map(m => m.routeId)];
        if (routeRemovals[vehicleId]) {
          currentRoutes = currentRoutes.filter(
            id => !routeRemovals[vehicleId].includes(id)
          );
        }
        const addedRoutes = routeAssignments[vehicleId] || [];
        currentRoutes = [...new Set([...currentRoutes, ...addedRoutes])];

        if (currentRoutes.length > 0) {
          currentRoutes.forEach(routeID => {
            payload.push({
              isChecked: true,
              routeId: parseInt(routeID),
              vechileID: parseInt(vehicleId),
            });
          });
        } else {
          payload.push({
            isChecked: true,
            routeId: null,
            vechileID: parseInt(vehicleId),
            clearAll: true
          });
        }
      });

      const response = await fetch(`${apiUrl}/Transportation/RouteMappingVehicle/RouteAssign`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw new Error(`Error: ${response.status}`);

      const data = await response.json();
      let successCount = 0;
      let failedCount = 0;

      if (Array.isArray(data)) {
        data.forEach(item => {
          if (
            item.msg &&
            (item.msg.toLowerCase().includes("success") ||
              item.msg.toLowerCase().includes("created") ||
              item.msg.toLowerCase().includes("assigned"))
          ) {
            successCount++;
          } else {
            failedCount++;
          }
        });
      }

      if (successCount > 0) {
        setSnackbar({
          open: true,
          message: `Successfully updated ${successCount} route(s)`,
          severity: "success",
        });
        setSelectedVehicles([]);
        setRouteAssignments({});
        setRouteRemovals({});
      }

      if (failedCount > 0) {
        setSnackbar({
          open: true,
          message: `${failedCount} update(s) failed.`,
          severity: "warning",
        });
      }
    } catch (error) {
      console.error("Error saving route assignments:", error);
      setSnackbar({
        open: true,
        message: "Failed to save route assignments",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  }, [selectedVehicles, routeAssignments, existingMappings, routeRemovals, canSave]);

  const handleSearch = useCallback((event) => {
    setSearchTerm(event.target.value);
    setPageNumber(1);
  }, []);

  const handlePageChange = useCallback((event, value) => {
    setPageNumber(value);
  }, []);

  const handleViewMappings = useCallback((vehicleId) => {
    setSelectedVehicleForView(vehicleId);
    if (vehicleId && vehicleId !== 0) {
      fetchRouteVehicleMappings(vehicleId);
    }
  }, [fetchRouteVehicleMappings]);

  const handleRefresh = useCallback(() => {
    setExistingMappings({});
    fetchVehicles();
    fetchRoutes();
  }, [fetchVehicles, fetchRoutes]);

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  useEffect(() => {
    fetchVehicles();
    fetchRoutes();
    if (formId) {
      Authorizer();
    }
  }, [fetchVehicles, fetchRoutes, formId]);

  const totalPages = Math.ceil(totalRecords / pageSize);
  const paginatedVehicles = vehicles.slice((pageNumber - 1) * pageSize, pageNumber * pageSize);
  const allCurrentPageSelected = paginatedVehicles.length > 0 &&
    paginatedVehicles.every(v => selectedVehicles.includes(v.vechileID));

  if (!authorization) {
    return <div>Loading...</div>;
  }

  if (authorization.isPermission !== 1 || !formId) {
    return <div>You are not authorized to view this page</div>;
  }

  return (
    <Container maxWidth="lg">
    <Box
          sx={{
            backgroundColor: '#0B1F40', // dark navy blue
            padding: '12px 24px',
            boxShadow: 3,
            borderRadius: '4px 4px 0 0',
            margin: '2px auto',
           maxWidth: "100%",
          }}
        >
          <Typography variant="h4" sx={{ color: '#fff', fontWeight: 'bold' }}>
        Route Vehicle Assignment   <InfoIcon onClick={() => setShowUserGuide(true)} style={{ opacity: "0.6" }} />
      </Typography> </Box>

       
      <StyledPaper elevation={3} style={{marginTop:6}}>
        
        <Dialog
          open={showUserGuide}
          onClose={() => setShowUserGuide(false)}
          maxWidth="sm"
          fullWidth
          sx={{
            "& .MuiDialog-paper": {
              borderRadius: "12px",
              boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
              backgroundColor: "#f9fafb",
            },
          }}
        >
          <DialogTitle
            sx={{
              background: "linear-gradient(90deg, #1898c2 0%, #beab6c 100%)",
              color: "white",
              fontWeight: "bold",
              textAlign: "center",
              borderRadius: "12px 12px 0 0",
              py: 2,
            }}
          >
            Route Vehicle Assignment User Guide
          </DialogTitle>
          <DialogContent sx={{ padding: "24px" }}>
            <Typography
              variant="h6"
              sx={{
                color: "#1898c2",
                fontWeight: "600",
                mb: 2,
                mt: 1,
                textAlign: "center",
              }}
            >
              Quick Steps to Map Vehicle Routes
            </Typography>
            <Box
              component="ul"
              sx={{
                listStyleType: "none",
                padding: 0,
                "& li": {
                  display: "flex",
                  alignItems: "flex-start",
                  mb: 1.5,
                  fontSize: "0.9rem",
                  color: "#333",
                },
                "& li::before": {
                  content: '"\\2022"',
                  color: "#1898c2",
                  fontWeight: "bold",
                  display: "inline-block",
                  width: "1.5em",
                  mr: 1,
                  fontSize: "1.2rem",
                },
                "& strong": {
                  margin: "0 0.2em",
                },
              }}
            >
              <li><strong>Search</strong> vehicles by registration, make, or chassis</li>
              <li><strong>Select</strong> vehicles using checkboxes</li>
              <li><strong>Choose routes</strong> from dropdown menus</li>
              <li><strong>Add/Remove</strong> routes using the + button and chips</li>
              <li><strong>Save</strong> assignments when ready</li>
              <li>Track <strong>active routes</strong> via colored chips</li>
            </Box>
          </DialogContent>
          <DialogActions sx={{ justifyContent: "center", pb: 3 }}>
            <StyledButton
              onClick={() => setShowUserGuide(false)}
              color="primary"
              variant="contained"
              sx={{
                backgroundColor: "#1898c2",
                "&:hover": { backgroundColor: "#156f9a" },
                borderRadius: "8px",
                px: 4,
              }}
            >
              Close
            </StyledButton>
          </DialogActions>
        </Dialog>
        {loading && (
          <Fade in={loading}>
            <CircularProgress sx={{ display: "block", mx: "auto", mb: 2 }} />
          </Fade>
        )}
        <Box sx={{ display: "flex", gap: 2, mb: 3, alignItems: "center" }}>
          <TextField
            label="Search Vehicles"
            variant="outlined"
            value={searchTerm}
            onChange={handleSearch}
            sx={{ flexGrow: 1, borderRadius: 2 }}
            placeholder="Search by registration, make, model, chassis..."
          />
          <Button
            onClick={handleRefresh}
            variant="outlined"
            color="primary"
            disabled={loading}
            style={{height:53}}
          >
            Refresh
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            color="primary"
            style={{height:53}}
            disabled={loading || selectedVehicles.length === 0 || !canSave}
          >
            {loading ? "Saving..." : `Assign ${selectedVehicles.length} Vehicle(s)`}
          </Button>
        </Box>
      </StyledPaper>
      <StyledPaper elevation={3} sx={{ mt: 1 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color:'#127498ff'}}>
          Vehicle List & Route Assignment
        </Typography>
       <TableContainer
  component={Paper}
  style={{
   
    marginTop: "16px",
    border: '1px solid #ccc'
  }}
>
  <Table>
    <TableHead>
      <TableRow>
        <TableCell sx={{ borderRight: '1px solid #ccc', borderBottom: '1px solid #ccc', fontWeight: 'bold', fontSize: '1.1rem', backgroundColor: '#f5f5f5' }}>
          Actions
        </TableCell>
        <TableCell sx={{ borderRight: '1px solid #ccc', borderBottom: '1px solid #ccc', fontWeight: 'bold', fontSize: '1.1rem', backgroundColor: '#f5f5f5' }}>
          Registration No
        </TableCell>
        <TableCell sx={{ borderRight: '1px solid #ccc', borderBottom: '1px solid #ccc', fontWeight: 'bold', fontSize: '1.1rem', backgroundColor: '#f5f5f5' }}>
          Make & Model
        </TableCell>
        <TableCell sx={{ borderRight: '1px solid #ccc', borderBottom: '1px solid #ccc', fontWeight: 'bold', fontSize: '1.1rem', backgroundColor: '#f5f5f5' }}>
          Chassis No
        </TableCell>
        <TableCell sx={{ borderRight: '1px solid #ccc', borderBottom: '1px solid #ccc', fontWeight: 'bold', fontSize: '1.1rem', backgroundColor: '#f5f5f5' }}>
          Fuel Type
        </TableCell>
        <TableCell sx={{ borderRight: '1px solid #ccc', borderBottom: '1px solid #ccc', fontWeight: 'bold', fontSize: '1.1rem', backgroundColor: '#f5f5f5' }}>
          Manufacturing Date
        </TableCell>
        <TableCell sx={{ borderRight: '1px solid #ccc', borderBottom: '1px solid #ccc', fontWeight: 'bold', fontSize: '1.1rem', backgroundColor: '#f5f5f5', minWidth: 200 }}>
          Assign Route
        </TableCell>
      </TableRow>
    </TableHead>
    <TableBody>
      {paginatedVehicles.map((vehicle) => {
        const vehicleMappings = existingMappings[vehicle.vechileID] || [];
        return (
          <TableRow
            key={vehicle.vechileID}
            sx={{
              borderTop: '1px solid #ccc',
              "&:hover": {
                backgroundColor: "#f5f5f5",
                transition: "background-color 0.2s",
              },
            }}
          >
            <TableCell sx={{ borderRight: '1px solid #ccc', fontSize: '1rem' }}>
              <Checkbox
                checked={selectedVehicles.includes(vehicle.vechileID)}
                onChange={(event) => {
                  handleVehicleSelection(vehicle.vechileID, event.target.checked);
                }}
                disabled={loading || !canEdit}
                sx={{ "&.Mui-checked": { color: "#1976d2" } }}
              />
            </TableCell>
            <TableCell sx={{ borderRight: '1px solid #ccc', fontSize: '1rem' }}>{vehicle.registrationNo}</TableCell>
            <TableCell sx={{ borderRight: '1px solid #ccc', fontSize: '1rem' }}>{vehicle.make} {vehicle.model}</TableCell>
            <TableCell sx={{ borderRight: '1px solid #ccc', fontSize: '1rem' }}>{vehicle.chassesNo || "Not Available"}</TableCell>
            <TableCell sx={{ borderRight: '1px solid #ccc', fontSize: '1rem' }}>{vehicle.fuelType || "Not Available"}</TableCell>
            <TableCell sx={{ borderRight: '1px solid #ccc', fontSize: '1rem' }}>
              {vehicle.dateOfMfg ? new Date(vehicle.dateOfMfg).toLocaleDateString() : "Not Available"}
            </TableCell>
            <TableCell sx={{ borderRight: '1px solid #ccc', fontSize: '1rem' }}>
              <FormControl fullWidth size="small">
                <Select
                  value={currentSelections[vehicle.vechileID] || ""}
                  onChange={(event) => handleTempRouteChange(vehicle.vechileID, event.target.value)}
                  displayEmpty
                  disabled={loading || !canEdit}
                  sx={{ minWidth: 180 }}
                >
                  <MenuItem value="">
                    <em>Select Route</em>
                  </MenuItem>
                  {routes.map((route) => (
                    <MenuItem key={route.routeID} value={route.routeID}>
                      {route.routeName} (₹{route.routeCharge} - {route.distance}km)
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                onClick={() => handleAddRoute(vehicle.vechileID)}
                variant="outlined"
                size="small"
                sx={{ mt: 1, ml: 1 }}
                disabled={!currentSelections[vehicle.vechileID] || !canEdit}
              >
                + Add Route
              </Button>
              <Box sx={{ display: "flex", flexWrap: "wrap", mt: 1 }}>
                {vehicleMappings.map((mapping) => (
                  <Chip
                    key={`existing-${mapping.routeVehicleID}`}
                    label={`${mapping.routeName} (₹${mapping.routeCharge} - ${mapping.distance}km)`}
                    onDelete={() => {
                      setRouteRemovals(prev => ({
                        ...prev,
                        [vehicle.vechileID]: [...(prev[vehicle.vechileID] || []), mapping.routeId]
                      }));
                      setExistingMappings(prev => ({
                        ...prev,
                        [vehicle.vechileID]: prev[vehicle.vechileID].filter(m => m.routeId !== mapping.routeId)
                      }));
                    }}
                    deleteIcon={<span style={{ cursor: 'pointer' }}>✕</span>}
                    sx={{ m: 0.5 }}
                    color="success"
                    variant="outlined"
                  />
                ))}
                {(routeAssignments[vehicle.vechileID] || []).map((routeID, index) => {
                  const route = routes.find(r => r.routeID === routeID);
                  return route ? (
                    <Chip
                      key={`added-${index}`}
                      label={`${route.routeName} (₹${route.routeCharge} - ${route.distance}km)`}
                      onDelete={() =>
                        setRouteAssignments((prev) => ({
                          ...prev,
                          [vehicle.vechileID]: prev[vehicle.vechileID].filter(id => id !== routeID)
                        }))
                      }
                      sx={{ m: 0.5 }}
                      color="primary"
                      variant="outlined"
                    />
                  ) : null;
                })}
              </Box>
            </TableCell>
          </TableRow>
        );
      })}
      {vehicles.length === 0 && !loading && (
        <TableRow>
          <TableCell colSpan={7} align="center" sx={{ py: 3, borderRight: '1px solid #ccc' }}>
            No vehicles found
          </TableCell>
        </TableRow>
      )}
    </TableBody>
  </Table>
</TableContainer>

        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 3 }}>
          <Box>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Total Vehicles: {totalRecords} | Selected: {selectedVehicles.length}
            </Typography>
          </Box>
          {totalPages > 1 && (
            <Pagination
              count={totalPages}
              page={pageNumber}
              onChange={handlePageChange}
              color="primary"
              disabled={loading}
            />
          )}
        </Box>
      </StyledPaper>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default RouteVehicleMapping;
